"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DomoClient = void 0;
var helpers_1 = require("src/helpers");
var WHITELISTED_ORIGINS = '^https?://([^/]+[.])?(domo|domotech|domorig).(com|io)?(/.*)?$';
var BLACKLISTED_ORIGINS = '(.*).(domoapps).(.*)';
var getDomoEnv = function () {
    var params = getQueryParams();
    var env = params.split('&').reduce(function (memo, param) {
        var _a = param.split('='), key = _a[0], arg = _a[1];
        memo[key] = decodeURIComponent(arg);
        return memo;
    }, {});
    return env;
};
var getQueryParams = function () { return self.location.search.substring(1); };
/**
 * Utilities for helping your app interact with the Domo environment.
 *
 * ## Basic Usage
 *
 * ```
 * import { DomoClient } from @domoinc/toolkit;
 *
 * // call client static methods
 * domo.getUserId();
 * ```
 */
var DomoClient = /** @class */ (function () {
    function DomoClient() {
    }
    /**
     * Domo Apps behave like standard cards in the fact that they'll automatically reload each time
     * the underlying datasets are updated. Depending on the app, this could have a negative impact
     * on the user experience of your app.
     *
     * This function allows you to listen for the data update events and control how your app behaves
     * when the dataSets are updated.
     *
     * ## Basic Usage
     *
     * ```typescript
     * import { DomoClient as domo } from @domoinc/toolkit;
     * import Query from @domoinc/query;
     *
     * domo.onDataUpdate((alias) => {
     *  console.info(`${alias} just updated`, new Date().getTime());
     *
     *  // now get the latest data and handle as you'd like
     *  (new Query()).get(alias).then((data) => {
     *
     *    // do something with the data
     *    console.table(data);   *
     *  });
     * });
     * ```
     */
    /* eslint-disable-next-line @typescript-eslint/ban-types */
    DomoClient.onDataUpdate = function (cb) {
        if (!(0, helpers_1.isFunction)(cb))
            throw new TypeError();
        var wrappedCallback = DomoClient.wrapDataUpdateCallback(cb);
        self.addEventListener('message', wrappedCallback);
        return function () {
            self.removeEventListener('message', wrappedCallback);
        };
    };
    /* eslint-disable-next-line @typescript-eslint/ban-types */
    DomoClient.onFiltersUpdate = function (cb) {
        if (!(0, helpers_1.isFunction)(cb))
            throw new TypeError();
        helpers_1.DomoEvents.bind('filtersUpdated', cb);
    };
    /**
     * Domo Apps have sandbox restrictions preventing the app from opening a new browser tab when
     * following links. As such, standard links will not work as expected inside Domo Apps.
     *
     * ```
     * <a href="www.google.com" target="_blank">Will Not Work</a>
     * ```
     *
     * If you're wanting to control the main browser you'll need to use this `navigate` function.
     *
     * ## Basic Usage
     *
     * ```html
     * <button onclick="openTab('www.google.com')">Open a new Window</button>
     * <button onclick="useCurrentTab('www.google.com')">Use Current Window</button>
     *
     * <script>
     *  function openTab(url) {
     *    domo.navigate(url);
     *  }
     *
     *  function useCurrentTab(url) {
     *    domo.navigate(url, false);
     *  }
     * </script>
     * ```
     */
    DomoClient.navigate = function (url, isNewWindow) {
        if (isNewWindow === void 0) { isNewWindow = true; }
        var message = JSON.stringify({ url: url, isNewWindow: isNewWindow, event: 'navigate' });
        self.parent.postMessage(message, '*');
    };
    DomoClient.getAppId = function () {
        var re = /^(.+?)\.domoapps.+/;
        var domain = DomoClient.getHostName();
        var matches = domain.match(re);
        return matches !== null ? matches[1] : domain;
    };
    DomoClient.getUserId = function () {
        return DomoClient.env.userId;
    };
    DomoClient.getUserEmail = function () {
        return DomoClient.env.userEmail;
    };
    DomoClient.getUserName = function () {
        return typeof DomoClient.env.userName !== 'undefined'
            ? DomoClient.env.userName.replace('+', ' ')
            : undefined;
    };
    DomoClient.getUserPlatform = function () {
        return DomoClient.env.platform;
    };
    DomoClient.getCustomerName = function () {
        return DomoClient.env.customer;
    };
    DomoClient.getCustomerEnvironment = function () {
        return DomoClient.env.environment;
    };
    DomoClient.getCustomerLocale = function () {
        return DomoClient.env.locale;
    };
    DomoClient.getHostName = function () {
        return self.location.hostname;
    };
    DomoClient.isVerifiedOrigin = function (origin) {
        var whitelisted = origin.match(WHITELISTED_ORIGINS);
        var blacklisted = origin.match(BLACKLISTED_ORIGINS);
        return !!whitelisted && !blacklisted;
    };
    /* eslint-disable-next-line @typescript-eslint/ban-types */
    DomoClient.wrapDataUpdateCallback = function (cb) {
        return function (event) {
            if ((0, helpers_1.isUndefined)(event) || !DomoClient.isVerifiedOrigin(event.origin))
                return;
            if ((0, helpers_1.isString)(event.data)) {
                try {
                    var message = JSON.parse(event.data);
                    if (!Boolean(message.hasOwnProperty('alias'))) {
                        return;
                    }
                    var alias = message.alias;
                    var ack = JSON.stringify({ alias: alias, event: 'ack' });
                    window.postMessage(ack, event.origin);
                    cb(alias);
                }
                catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn("\n            There was an error in domo.onDataUpdate! It may be that our event\n            listener caught a message from another source and tried to parse it,\n            so your update still may have worked.\n\n            If you would like more info, here is the error: ".concat(err.message, "\n          "));
                }
            }
        };
    };
    DomoClient.env = getDomoEnv();
    DomoClient.getPageFilters = function () {
        try {
            var url = new URL(window.location.href);
            var filters = url.searchParams.get('analyzer');
            if (filters !== null) {
                var parsed = JSON.parse(filters);
                if (Boolean(parsed) || !Array.isArray(parsed))
                    return parsed;
            }
        }
        catch (_a) {
            // eslint-disable-next-line no-console
            console.warn('Could not parse page filters');
        }
        return [];
    };
    DomoClient.writePageFilters = function (filters) {
        var a = document.createElement('a');
        a.href = window.location.href;
        var search = (0, helpers_1.querystring)(a.search);
        var analyzer = JSON.stringify(filters);
        search.analyzer = analyzer;
        a.search = (0, helpers_1.querystringify)(search, '?');
        DomoClient.env.analyzer = analyzer;
        // write filters to url so they're in Referer header
        window.history.replaceState(null, 'filteredApp', a.href);
    };
    return DomoClient;
}());
exports.DomoClient = DomoClient;
